import { getTravellerTypeAtTravelDate } from "@/features/travellers/utils/ageValidation";
import type {
  FindUserTravelersTravelers,
  TemporallyStoreHotelBookingDetailsTraveler,
} from "@/types/codegen";
import { getDateString } from "@/utils/dates";

export class TravellerAssignmentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TravellerAssignmentError";
  }
}

type RoomToAssign = {
  token: string;
  adults: number;
  children: number;
};

/**
 * Automatically assigns travellers to rooms based on required adult/child counts.
 *
 * For each room, the first available traveller with a matching age group
 * (adult or non-adult) is selected. A traveller can only appear in one room.
 *
 * @throws {TravellerAssignmentError} If the number of adult travellers does not exactly match the total adult slots across all rooms.
 * @throws {TravellerAssignmentError} If the number of non-adult travellers does not exactly match the total children slots across all rooms.
 */
export function assignTravellersToRooms(
  rooms: RoomToAssign[],
  selectedTravellers: FindUserTravelersTravelers[],
  leadTravellerId: string,
  travelDate: Date,
): {
  packageRoomToken: string;
  travelers: TemporallyStoreHotelBookingDetailsTraveler[];
}[] {
  const { totalAdultsNeeded, totalChildrenNeeded } = rooms.reduce(
    (acc, r) => ({
      totalAdultsNeeded: acc.totalAdultsNeeded + r.adults,
      totalChildrenNeeded: acc.totalChildrenNeeded + r.children,
    }),
    { totalAdultsNeeded: 0, totalChildrenNeeded: 0 },
  );

  const { adultTravellers, nonAdultTravellers } = selectedTravellers
    .map((traveler) => ({
      ...traveler,
      lead: traveler.id === leadTravellerId,
      type: getTravellerTypeAtTravelDate(traveler.birthDate, travelDate),
      birthDate: getDateString(traveler.birthDate),
      emailAddress: traveler.emailAddress || undefined,
      phoneNumber: traveler.phoneNumber.replace(traveler.phonePrefix, ""),
    }))
    .reduce(
      (acc, t) => {
        if (t.type === "adult") {
          acc.adultTravellers.push(t);
        } else {
          acc.nonAdultTravellers.push(t);
        }
        return acc;
      },
      {
        adultTravellers: [] as TemporallyStoreHotelBookingDetailsTraveler[],
        nonAdultTravellers: [] as TemporallyStoreHotelBookingDetailsTraveler[],
      },
    );

  if (adultTravellers.length !== totalAdultsNeeded) {
    throw new TravellerAssignmentError(
      `Expected ${totalAdultsNeeded} adult traveller(s) but got ${adultTravellers.length}.`,
    );
  }
  if (nonAdultTravellers.length !== totalChildrenNeeded) {
    throw new TravellerAssignmentError(
      `Expected ${totalChildrenNeeded} non-adult traveller(s) but got ${nonAdultTravellers.length}.`,
    );
  }

  return rooms.map(
    ({ token, adults: adultsNeeded, children: childrenNeeded }) => ({
      packageRoomToken: token,
      travelers: [
        ...adultTravellers.splice(0, adultsNeeded),
        ...nonAdultTravellers.splice(0, childrenNeeded),
      ],
    }),
  );
}
